import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { transactionAPI, beneficiaryAPI, departmentAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Send, Wallet, ArrowRight, ArrowLeft, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const InitiatePayment = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [department, setDepartment] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  
  const [formData, setFormData] = useState({
    beneficiary_id: '',
    amount: '',
    description: '',
    reference_number: ''
  });

  const [selectedBenef, setSelectedBenef] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [deptRes, benefRes] = await Promise.all([
        departmentAPI.list(),
        beneficiaryAPI.list()
      ]);
      setDepartment(deptRes.data[0]);
      setBeneficiaries(benefRes.data.filter(b => !b.is_blacklisted));
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleBenefChange = (benefId) => {
    setFormData({ ...formData, beneficiary_id: benefId });
    const benef = beneficiaries.find(b => b.beneficiary_id === benefId);
    setSelectedBenef(benef);
  };

  const handleNext = () => {
    if (!formData.beneficiary_id) {
      toast.error('Please select a beneficiary');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!formData.description) {
      toast.error('Please enter a description');
      return;
    }
    
    const amount = parseFloat(formData.amount);
    if (amount > (department?.balance || 0)) {
      toast.error('Insufficient balance');
      return;
    }
    
    setStep(2);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await transactionAPI.create({
        beneficiary_id: formData.beneficiary_id,
        amount: parseFloat(formData.amount),
        description: formData.description,
        reference_number: formData.reference_number || null
      });
      toast.success('Payment submitted for approval!');
      navigate('/department/payments');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount || 0);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto" data-testid="initiate-payment-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Outfit']">Initiate Payment</h1>
          <p className="text-gray-500 mt-1">Send money to your beneficiaries</p>
        </div>

        {/* Balance Card */}
        <Card className="glass-card mb-6">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Wallet className="w-6 h-6 text-blue-600" />
              <span className="text-gray-500">Available Balance</span>
            </div>
            <span className="text-xl font-bold text-blue-600 font-['JetBrains_Mono']">
              {formatCurrency(department?.balance)}
            </span>
          </CardContent>
        </Card>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-900 text-gray-900' : 'bg-gray-50 text-gray-500'}`}>1</div>
          <div className={`w-20 h-0.5 ${step >= 2 ? 'bg-blue-900' : 'bg-gray-50'}`} />
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-900 text-gray-900' : 'bg-gray-50 text-gray-500'}`}>2</div>
        </div>

        {/* Step 1: Payment Details */}
        {step === 1 && (
          <Card className="glass-card animate-fadeInUp">
            <CardHeader className="border-b border-gray-200">
              <CardTitle className="text-lg text-gray-900">Payment Details</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-500">Beneficiary</Label>
                <Select value={formData.beneficiary_id} onValueChange={handleBenefChange}>
                  <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-900" data-testid="beneficiary-select">
                    <SelectValue placeholder="Select a beneficiary" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-50 border-gray-300">
                    {beneficiaries.length > 0 ? (
                      beneficiaries.map(b => (
                        <SelectItem key={b.beneficiary_id} value={b.beneficiary_id} className="text-gray-900">
                          {b.name} - {b.upi_id || b.account_number}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled className="text-gray-500">No beneficiaries</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {beneficiaries.length === 0 && (
                  <p className="text-sm text-amber-500">Add beneficiaries first in Beneficiaries section</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-gray-500">Amount</Label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  placeholder="0.00"
                  className="bg-gray-50 border-gray-300 text-gray-900 font-['JetBrains_Mono'] text-lg h-12"
                  data-testid="amount-input"
                />
                {formData.amount && parseFloat(formData.amount) > (department?.balance || 0) && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> Exceeds available balance
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-gray-500">Description / Purpose</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Payment for services..."
                  className="bg-gray-50 border-gray-300 text-gray-900 min-h-20"
                  data-testid="description-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-500">Reference Number (Optional)</Label>
                <Input
                  value={formData.reference_number}
                  onChange={(e) => setFormData({...formData, reference_number: e.target.value})}
                  placeholder="INV-001"
                  className="bg-gray-50 border-gray-300 text-gray-900"
                />
              </div>

              <Button
                onClick={handleNext}
                className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-gray-900 mt-4"
                disabled={beneficiaries.length === 0}
                data-testid="next-btn"
              >
                Continue <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Review & Confirm */}
        {step === 2 && (
          <Card className="glass-card animate-fadeInUp">
            <CardHeader className="border-b border-gray-200">
              <CardTitle className="text-lg text-gray-900">Review & Confirm</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4 mb-6">
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-500">Beneficiary</span>
                  <span className="text-gray-900 font-medium">{selectedBenef?.name}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-500">Account Details</span>
                  <span className="text-gray-900 font-['JetBrains_Mono'] text-sm">{selectedBenef?.upi_id || selectedBenef?.account_number}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-500">Amount</span>
                  <span className="text-gray-900 font-['JetBrains_Mono'] text-xl">{formatCurrency(parseFloat(formData.amount))}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-500">Description</span>
                  <span className="text-gray-900 text-right max-w-xs">{formData.description}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-gray-500">Balance After</span>
                  <span className="text-blue-600 font-['JetBrains_Mono']">
                    {formatCurrency((department?.balance || 0) - parseFloat(formData.amount))}
                  </span>
                </div>
              </div>

              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mb-6">
                <p className="text-amber-400 text-sm">
                  This payment will be submitted for approval. Once approved, the amount will be deducted from your department balance.
                </p>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1 border-gray-300 text-gray-600">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  className="flex-1 bg-blue-900 hover:bg-blue-800 text-gray-900 glow-primary"
                  disabled={submitting}
                  data-testid="submit-payment-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Submitting...</>
                  ) : (
                    <><Send className="w-5 h-5 mr-2" /> Submit for Approval</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default InitiatePayment;
